import React from "react";
import { Link } from "react-router-dom";
import DiscoveryLayout, { Faq, SourceLink } from "../../components/discovery/DiscoveryLayout";
import CleanSweepsPlans from "../../components/discovery/CleanSweepsPlans";

const sources = {
  listing: "https://apps.shopify.com/viralsweep",
  limits: "https://support.viralsweep.com/en/articles/16074482-purchases-app-how-the-order-limit-works",
  setup: "https://support.viralsweep.com/en/articles/9272785-shopify-purchase-promotions-setup",
  bonuses: "https://support.viralsweep.com/en/articles/9272037-product-bonus-and-multipliers",
  freeEntry: "https://support.viralsweep.com/en/articles/13691568-connect-a-purchase-promotion-to-a-regular-sweepstakes-promotion",
  overview: "https://support.viralsweep.com/en/articles/14436067-viralsweep-for-shopify-getting-started-guide",
};

const viralSweepPlans = [
  { name: "Starter", price: 49, capacity: "100 unique customers per promotion" },
  { name: "Business", price: 199, capacity: "1,000 unique customers per promotion" },
  { name: "Premium", price: 399, capacity: "5,000 unique customers per promotion" },
  { name: "Elite", price: 999, capacity: "Unlimited purchase-promotion capacity" },
];

export default function ViralSweepComparison() {
  return (
    <DiscoveryLayout
      path="/compare/clean-sweeps-vs-viralsweep"
      title="Clean Sweeps vs ViralSweep for Shopify sweepstakes"
      description="Compare Clean Sweeps and ViralSweep pricing, purchase-entry limits, storefront displays, referrals, AMOE workflows and first-campaign setup."
      eyebrow="Shopify sweepstakes app comparison"
      sections={[
        { id: "which-app", label: "Which app fits?" },
        { id: "pricing", label: "Pricing and capacity" },
        { id: "features", label: "Entry and display features" },
        { id: "switching", label: "Plan your first campaign" },
        { id: "frequently-asked-questions", label: "Common questions" },
      ]}
    >
      <section id="which-app">
        <h2>Choose around the campaign you actually want to run</h2>
        <p>
          If your Shopify business sells merchandise and awards sweepstakes entries
          with qualifying purchases, Clean Sweeps is a strong place to start. You
          can configure entries per dollar, show product entry estimates, manage
          entrants and add more displays and reward types as your campaign grows.
          Its Free plan lets you start with a small order allowance, and the
          first-campaign setup offer below provides help with configuration and
          standard widget placement.
        </p>
        <p>
          ViralSweep is worth considering when your marketing program also calls
          for photo or video contests, instant-win promotions, waitlists or other
          promotion formats. Its purchase promotion tool also supports Shopify
          orders. The useful comparison is the workflow and plan you need, rather
          than the number of features on a general marketing checklist.{" "}
          <SourceLink href={sources.overview}>ViralSweep product overview</SourceLink>.
        </p>
      </section>

      <section id="pricing">
        <h2>Compare pricing with the correct capacity unit</h2>
        <p>
          <strong>Clean Sweeps measures processed orders per month. ViralSweep
          measures unique purchasing customers per promotion.</strong> These are
          different limits. For example, 80 customers placing two orders each
          during one promotion represent 80 unique customers and 160 orders.
          Campaign duration and repeat purchases matter when you select a plan.
        </p>
        <CleanSweepsPlans />
        <h3>ViralSweep monthly plans</h3>
        <div className="discovery-table-scroll" role="region" aria-label="ViralSweep monthly pricing and capacity" tabIndex={0}>
          <table>
            <caption>ViralSweep monthly USD pricing and purchase-promotion capacity</caption>
            <thead>
              <tr><th scope="col">Plan</th><th scope="col">Monthly price</th><th scope="col">Purchase capacity</th></tr>
            </thead>
            <tbody>
              {viralSweepPlans.map((plan) => (
                <tr key={plan.name}>
                  <th scope="row">{plan.name}</th>
                  <td>${plan.price.toLocaleString("en-US")}/month</td>
                  <td>{plan.capacity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          ViralSweep&apos;s listing labels purchase capacity as “orders”; its
          support documentation explains the unique-customer, per-promotion
          calculation. The listing includes Purchases on Starter and a seven-day
          trial. Annual billing is also offered. Verify the current plan before
          subscribing.{" "}
          <SourceLink href={sources.listing}>ViralSweep pricing</SourceLink>;{" "}
          <SourceLink href={sources.limits}>capacity calculation</SourceLink>.
        </p>
        <p>
          For Clean Sweeps, select capacity for your expected monthly processed
          orders, then check the features you want to launch with. A low-volume
          product display, a cart-based campaign and a campaign with referral
          rewards need different feature access even when their order counts are
          similar. The Free plan does not include in-app winner selection or AMOE;
          those begin on Basic and Diamond respectively.
        </p>
      </section>

      <section id="features">
        <h2>Purchase entries, storefront displays and campaign management</h2>
        <div className="discovery-table-scroll" role="region" aria-label="Clean Sweeps and ViralSweep feature comparison" tabIndex={0}>
          <table>
            <caption>Documented capabilities; availability depends on the selected plan</caption>
            <thead>
              <tr><th scope="col">Merchant need</th><th scope="col">Clean Sweeps</th><th scope="col">ViralSweep</th></tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Entries from merchandise purchases</th>
                <td>Configurable entries per dollar and product entry rules; product entry widget on Free and above.</td>
                <td>Configurable spend-based entries and eligible products. <SourceLink href={sources.setup}>Setup documentation</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Bonus promotions</th>
                <td>Product bonuses and scheduled promotions; special product and order multipliers on Platinum and above.</td>
                <td>Product bonuses, multipliers and scheduled bonus periods. <SourceLink href={sources.bonuses}>Bonus documentation</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Entry displays</th>
                <td>Product widget on Free; account widget on Basic; cart, guest lookup and post-checkout summary on Gold; checkout extensions on Platinum, with Shopify Plus required for checkout placement.</td>
                <td>Purchase widgets for confirmation/order-status and account surfaces. <SourceLink href={sources.setup}>Installation requirements</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Referral and customer rewards</th>
                <td>Referral codes, membership entry rules and confirmed email signup rewards on Platinum and above.</td>
                <td>Referral and social actions, with separate waitlist and milestone formats. <SourceLink href={sources.overview}>Promotion formats</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">No-purchase entry workflow</th>
                <td>Diamond includes an AMOE request form with pending review and approval.</td>
                <td>Can connect a purchase promotion with a free-entry sweepstakes promotion to combine entrants. <SourceLink href={sources.freeEntry}>Connected promotions</SourceLink>.</td>
              </tr>
              <tr>
                <th scope="row">Hands-on setup</th>
                <td>Free first-campaign configuration and eligible standard-widget placement for new subscribers, including Free; Diamond includes broader white-glove services.</td>
                <td>Elite lists white-glove service and one promotion built per month. <SourceLink href={sources.listing}>Plan details</SourceLink>.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Clean Sweeps feature references: <Link to="/#pricing">plan comparison</Link>,{" "}
          <Link to="/sweepstakes-entry-widgets">widget reference</Link> and{" "}
          <Link to="/setup">setup tutorials</Link>. In either app, test the actual
          theme and customer journey before promoting a new entry offer. A
          product estimate, a cart estimate and an awarded-entry total serve
          different purposes; make their labels clear to customers.
        </p>
      </section>

      <section id="switching">
        <h2>Start with a campaign specification, then test the complete journey</h2>
        <ol>
          <li>Write down eligible products, entry rates, dates and any bonus periods. Use the same example orders when evaluating each app.</li>
          <li>Choose where customers should see entry information: product, cart, account, post-checkout or checkout. Confirm your app and Shopify plan support each placement.</li>
          <li>Decide how free-entry requests will be received, reviewed and recorded under your official rules. An app&apos;s AMOE feature does not by itself make a promotion legally compliant.</li>
          <li>Check a qualifying order, an excluded product, a bonus-period order and a customer entry lookup. Review the final entrant records before launch.</li>
        </ol>
        <p>
          When switching from ViralSweep, retain your existing campaign records
          and agree on a clear transition date. Do not assume importing records
          also transfers campaign rules, winner history or storefront settings.
          The first-campaign setup offer covers a new Clean Sweeps configuration;
          historical entry migration is outside that offer. Starting the next
          campaign with a tested configuration is easier to review than changing
          the system underneath an active promotion.
        </p>
      </section>

      <Faq items={[
          {
            question: "Is Clean Sweeps a ViralSweep alternative for an LGND-style giveaway?",
            answer: "Yes, when you mean a merchandise promotion with qualifying purchase entries, product displays and entrant management. Choose the Clean Sweeps plan for your processed-order volume and required features. That does not mean Clean Sweeps powers LGND or that either app supplies your campaign's legal approval.",
          },
          {
            question: "Can I compare the two apps using the same order count?",
            answer: "Not directly. Clean Sweeps uses processed orders per month, while ViralSweep's purchase capacity uses unique customers per promotion. Forecast both figures, then compare the plans that support your actual campaign.",
          },
          {
            question: "Does a free-entry feature replace a sweepstakes administrator?",
            answer: "No. A form and entry-management workflow do not replace legal review, required filings, winner verification or prize fulfillment. Decide which of those services your campaign needs separately from the app subscription.",
          },
      ]} />
    </DiscoveryLayout>
  );
}
